/**
 * @fileoverview Define shared time, coverage, and source-state semantics for work comparisons. `WorkComparison.js` and `WorkComparisonLayouts.js` consume these rules so summary and detail views agree. A day can be future, unavailable, unlinked, or observed; missing duration is separate from zero. Timezone-local days and recorded weeks connect source timestamps to the user's comparison range.
 */

import moment from 'moment-timezone';

export const SOURCES = [
    { key: 'clockify', label: 'Clockify', unit: 'recorded', icon: '◷' },
    { key: 'documents', label: 'Documents', unit: 'versions', icon: '▤' },
    { key: 'trello', label: 'Trello', unit: 'actions', icon: '▥' },
];



export function dayKey(at, timezone) {
    if (!at) return '';
    const date = new Date(at);
    if (Number.isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
    const field = 

    (type) => parts.find((part) => part.type === type)?.value;
    return `${field('year')}-${field('month')}-${field('day')}`;
}



export function recordedWeek(at, timezone) {
    const monday = new Date(`${dayKey(at, timezone)}T12:00:00Z`);
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
}



export function formatTime(seconds, exact = false) {
    if (!Number.isFinite(seconds) || seconds < 0) return 'Duration unavailable';
    const minutes = Math.floor(Number(seconds) / 60);
    return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m${exact ? ` ${seconds % 60}s` : ''}`;
}

/**
 * Build bounded day summaries while retaining future/unavailable coverage and unknown-duration distinctions. Consumers must preserve those states rather than fill missing days with zero work.
 */
export function comparisonDays(data, now = new Date()) {
    if (!data) return [];
    const days = [];
    const cursor = new Date(`${data.start}T12:00:00Z`);
    const end = new Date(`${data.end}T12:00:00Z`);
    const today = dayKey(now, data.timezone);
    while (cursor <= end && days.length < 31) {
        const key = cursor.toISOString().slice(0, 10);
        const sources = {};
        const isFuture = key > today;
        const dayStart = moment.tz(key, data.timezone).startOf('day').valueOf();
        const dayEnd = moment.tz(key, data.timezone).startOf('day').add(1, 'day').valueOf();
        SOURCES.forEach(

        ({ key: source }) => {
            const state = data.sources?.[source];
            if (state?.daily) {
                const summary = state.daily[key] || { status: 'unavailable', value: null };
                sources[source] = { ...state, ...summary, records: [],
                    ...(isFuture ? { status: 'future', value: null } : {}) };
                return;
            }
            const records = (state?.records || []).filter((row) => dayKey(row.at, data.timezone) === key);
            const missingDuration = records.some((row) => !Number.isFinite(row.duration_seconds) || row.duration_seconds < 0);
            let status = state?.status || 'unavailable';
            if (isFuture) status = 'future';
            else if (isAvailable(state) && state.coverage_start && state.coverage_end) {
                const from = new Date(state.coverage_start).getTime();
                const through = new Date(state.coverage_end).getTime();
                if (dayEnd <= from || dayStart >= through) status = 'unavailable';
                else status = from > dayStart || through < Math.min(dayEnd, now.getTime()) || state.truncated ? 'partial' : 'available';
            }
            const value = !['available', 'partial'].includes(status) || (status === 'partial' && !records.length) ? null
                : source === 'clockify' ? missingDuration ? null : records.reduce((sum, row) => sum + row.duration_seconds, 0) : records.length;
            sources[source] = { ...state, records, value, status };
        });
        days.push({ key, sources, isFuture, isToday: key === today });
        cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return days;
}



export function isAvailable(source) {
    return source && ['available', 'partial'].includes(source.status);
}



export function sourceState(source) {
    return source?.status === 'future' ? 'Future' : source?.status === 'unmapped' ? 'Not linked' : 'Unavailable';
}



export function chartMaximum(value) {
    if (!(value > 0)) return 1;
    const base = 10 ** Math.floor(Math.log10(value));
    return [1, 2, 3, 5, 10].find(n => n * base >= value) * base;
}
